import React, { useMemo, useState } from 'react'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiAward, FiDownload, FiEye } = FiIcons

const templates = [
  { id: 'course', name: 'Course Completion', description: 'Celebrate completion of an individual course.' },
  { id: 'grade', name: 'Grade Completion', description: 'Recognize mastery for the full grade level.' },
  { id: 'diploma', name: 'Diploma', description: 'Official diploma for program completion.' },
]

const Certificates = () => {
  const [selectedTemplate, setSelectedTemplate] = useState('course')
  const [formData, setFormData] = useState({
    student: 'Ava Montgomery',
    date: '2024-09-15',
    signer: 'Head of School',
    course: 'Mathematics II'
  })

  const preview = useMemo(() => ({
    ...formData,
    templateName: templates.find(t => t.id === selectedTemplate)?.name
  }), [formData, selectedTemplate])

  const exportCertificate = () => {
    const text = `${preview.templateName}
Student: ${preview.student}
Course: ${preview.course}
Date: ${preview.date}
Signature: ${preview.signer}`
    const blob = new Blob([text], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${preview.student}-${selectedTemplate}-certificate.pdf`
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-[#00AAFF] to-[#1B4C87] rounded-2xl p-8 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm uppercase tracking-wider text-blue-100">Records</p>
            <h1 className="text-3xl font-bold">Certificates & Diplomas</h1>
            <p className="text-blue-100 mt-2">Customize templates, preview, and export official PDFs.</p>
          </div>
          <SafeIcon icon={FiAward} className="w-12 h-12 text-white opacity-80" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-md p-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-800">Templates</h3>
          <div className="space-y-3">
            {templates.map(template => (
              <label key={template.id} className={`block p-3 border rounded-lg cursor-pointer ${selectedTemplate === template.id ? 'border-[#00AAFF] bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-800">{template.name}</p>
                    <p className="text-sm text-gray-500">{template.description}</p>
                  </div>
                  <input
                    type="radio"
                    name="template"
                    checked={selectedTemplate === template.id}
                    onChange={() => setSelectedTemplate(template.id)}
                    className="text-[#00AAFF] focus:ring-[#00AAFF]"
                  />
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6 lg:col-span-2 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Student Name</p>
              <input
                type="text"
                value={formData.student}
                onChange={(e) => setFormData({ ...formData, student: e.target.value })}
                className="mt-1 w-full border border-gray-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
              />
            </div>
            <div>
              <p className="text-sm text-gray-600">Date</p>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="mt-1 w-full border border-gray-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
              />
            </div>
            <div>
              <p className="text-sm text-gray-600">Signer</p>
              <input
                type="text"
                value={formData.signer}
                onChange={(e) => setFormData({ ...formData, signer: e.target.value })}
                className="mt-1 w-full border border-gray-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
              />
            </div>
            <div>
              <p className="text-sm text-gray-600">Course/Program</p>
              <input
                type="text"
                value={formData.course}
                onChange={(e) => setFormData({ ...formData, course: e.target.value })}
                className="mt-1 w-full border border-gray-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
              />
            </div>
          </div>

          <div className="p-6 bg-gray-50 rounded-lg border border-dashed border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-gray-500 uppercase tracking-wider">{preview.templateName}</p>
                <h3 className="text-2xl font-bold text-gray-800">{preview.student}</h3>
              </div>
              <SafeIcon icon={FiEye} className="w-6 h-6 text-[#00AAFF]" />
            </div>
            <p className="text-gray-700 mb-2">This certificate is awarded to <span className="font-semibold">{preview.student}</span> for successfully completing <span className="font-semibold">{preview.course}</span>.</p>
            <div className="flex items-center justify-between mt-6 text-sm text-gray-600">
              <span>{preview.date}</span>
              <span>Signature: {preview.signer}</span>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={exportCertificate}
              className="inline-flex items-center px-4 py-2 bg-[#00AAFF] text-white rounded-lg hover:bg-[#0088cc]"
            >
              <SafeIcon icon={FiDownload} className="w-4 h-4 mr-2" />
              Download PDF
            </button>
            <div className="text-sm text-gray-500">
              Templates include course completion, grade completion, and diploma layouts.
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Certificates
